import { useMemo, useState } from "react";
import {
  useLazyGetUploadSignedUrlForTradeDataQuery,
  useLazyGetUploadSignedUrlQuery,
} from "@/services";
import { useSnackbar } from "@/hooks";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

const useUploadFile = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { selectedClient } = useAppSelector((state) => state.client);
  const isAdmin= useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user]);
  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [getUploadSignedUrl, { isLoading }] = useLazyGetUploadSignedUrlQuery();
  const [getUploadSignedUrlForTradeData, { isLoading: isLoadingTradeData }] =
    useLazyGetUploadSignedUrlForTradeDataQuery();
  const { show } = useSnackbar();
  const uploadFile = async (file: File) => {
    const { type, name } = file;
    setUploadProgress(0);

    try {
      const { signedUrl, key } = await getUploadSignedUrl({
        fileName: name,
        contentType: type,
      }).unwrap();

      const response = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": type,
        },
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      setUploadProgress(100);
      return { key };
    } catch (error) {
      if (error instanceof Error) {
        show("Filed to upload file", error.message, "error");
        setUploadProgress(100);
      }
    }
  };
  const uploadFileTradeData = async (
    file: File,
    index?: string,
    timeStamp?: string
  ) => {
    const { type, name } = file;
    setUploadProgress(0);

    try {
      const { signedUrl, key } = await getUploadSignedUrlForTradeData({
        fileName: name,
        contentType: type,
        index,
        clientId: isTDAdminTCD ? selectedTenant?._id : selectedClient?._id ?? user?.tenantId,
        ...(isAdmin && { tenantId: selectedTenant?._id }),
        timeStamp,
      }).unwrap();
      const response = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": type,
        },
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      setUploadProgress(100);
      return { key, timeStamp: timeStamp || new Date().toISOString() };
    } catch (error) {
      if (error instanceof Error) {
        show("Filed to upload file", error.message, "error");
        setUploadProgress(100);
      }
    }
  };

  return {
    uploadFile,
    isLoading,
    uploadProgress,
    uploadFileTradeData,
    isLoadingTradeData,
  };
};

export default useUploadFile;
